import {Metadata, RemoteConstantsModel} from "../models/remoteConstants.model";
import {AttributeManifestEntry} from "../models/attributeManifest.model";
import {MetadataCollection} from "../commands/metadataValue.command";
import {NavTarget} from "./navTarget";
import {isMissingLocalization} from "./localization.utils";

// Locate a metadata entry by uid and report which collection it lives in.
// Used to resolve a metadata's kind/destination from a bare uid (navigation,
// icon gating, etc.). Returns null if no collection contains the uid.
export function findMetadata(state: RemoteConstantsModel, uid: string): {id: string; collection: MetadataCollection} | null {
    const collections: [Metadata[] | undefined, MetadataCollection][] = [
        [state.unitAttributes, MetadataCollection.Units],
        [state.buildingAttributes, MetadataCollection.Buildings],
        [state.techActionAttributes, MetadataCollection.TechActions],
        [state.techPassiveAttributes, MetadataCollection.TechPassives],
        [state.rewards, MetadataCollection.Rewards],
    ];
    for (const [col, collection] of collections) {
        if (!col) continue;
        const meta = col.find(m => m.uid === uid);
        if (meta) return {id: meta.id, collection};
    }
    return null;
}

// Resolve a bare entity id / metadata uid to a Ctrl/Cmd-click navigation target
// by scanning the collections: units/buildings/tiles/resources match by id,
// metadata (attributes / tech actions / passives / rewards) by uid. Tech-tree
// NODE references are not yet navigable, so an unrecognised ref returns
// undefined (no-op).
export function resolveEntityRef(state: RemoteConstantsModel, ref: string): NavTarget | undefined {
    if (state.units?.some(u => u.id === ref)) return {kind: 'unit', id: ref};
    if (state.buildings?.some(b => b.id === ref)) return {kind: 'building', id: ref};
    if (state.tiles?.some(t => t.id === ref)) return {kind: 'tile', id: ref};
    if (state.resources?.some(r => r.id === ref)) return {kind: 'resource', id: ref};
    if (findMetadata(state, ref)) return {kind: 'metadata', uid: ref};
    return undefined;
}

export interface MetadataDisplayInfo {
    suffix: string;
    tooltip: string | undefined;
}

// Bracketed summary of a metadata's authored parameters, for compact labels —
// e.g. a SpawnUnitUpgrade reward reads "Spawn [Giant]". Uses the manifest entry
// (when present) to order fields like the form does and to map enum integers to
// member names; entity references are localized to their display name. Enum
// params always show (falling back to the default member when unauthored) so an
// Improvement reward reads "Improvement [Walls]" even before it's edited;
// numeric params appear only when authored. Returns '' when there is nothing to
// show.
export function getParameterSuffix(
    meta: { values?: { [key: string]: number }, entities?: { [key: string]: string } } | undefined,
    manifestEntry: AttributeManifestEntry | undefined,
    getLocalizationValue: (key: string) => string
): string {
    if (!meta) return '';
    const parts: string[] = [];

    if (manifestEntry) {
        for (const p of manifestEntry.Parameters) {
            if (p.Type === 'enum' && p.EnumValues) {
                const idx = (meta.values?.[p.Name] ?? p.DefaultValue) as number;
                if (p.EnumValues[idx] !== undefined) parts.push(p.EnumValues[idx]);
                continue;
            }
            const v = meta.values?.[p.Name];
            if (v === undefined) continue;
            parts.push(String(v));
        }
        for (const e of manifestEntry.Entities) {
            const v = meta.entities?.[e.Name];
            if (v) parts.push(getLocalizationValue(v));
        }
    } else {
        for (const v of Object.values(meta.values ?? {})) parts.push(String(v));
        for (const v of Object.values(meta.entities ?? {})) if (v) parts.push(getLocalizationValue(v));
    }

    return parts.length ? ' [' + parts.join(', ') + ']' : '';
}

export function getMetadataDisplayInfo(
    meta: Metadata | undefined,
    isDuplicate: boolean,
    getLocalizationValue: (key: string) => string
): MetadataDisplayInfo {
    const valueEntries = meta?.values ? Object.entries(meta.values) : [];

    const suffix = isDuplicate && valueEntries.length > 0
        ? ' [' + valueEntries.map(([, v]) => v).join(', ') + ']'
        : '';

    const desc = meta?.id ? getLocalizationValue(`${meta.id}.description`) : '';
    const descDisplay = isMissingLocalization(desc) ? '' : desc; // don't show the missing-key marker in tooltips
    const paramLines = valueEntries.map(([k, v]) => `${k}: ${v}`).join('\n');
    const tooltip = [descDisplay, paramLines].filter(s => s).join('\n\n') || undefined;

    return { suffix, tooltip };
}

export function getDuplicateIds(allMetadata: Metadata[]): Set<string> {
    const counts = new Map<string, number>();
    for (const m of allMetadata) {
        counts.set(m.id, (counts.get(m.id) ?? 0) + 1);
    }
    const dupes = new Set<string>();
    counts.forEach((count, id) => {
        if (count > 1) dupes.add(id);
    });
    return dupes;
}